import { useRef, useState } from 'react';
import { toast } from 'react-toastify';

import useMailboxService from 'src/modules/mailer/hooks/useMailboxService';
import {
    findMailboxLinkedCase,
    getMailboxIntentItems,
    isMailboxAnalysisNotRelevant,
    mailboxMessageHasLinkedCase,
    mapMailboxAnalyzeError,
    normalizeMailboxAnalysis,
} from '../helpers';

const useMailboxIntentPreview = ({
    t,
    intents,
    applyCase,
}) => {
    const service = useMailboxService();
    const resolvedIntents = intents?.length ? intents : getMailboxIntentItems();
    const [session, setSession] = useState(null);
    const [analysis, setAnalysis] = useState(null);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [isApplying, setIsApplying] = useState(false);
    const requestRef = useRef(0);
    const isApplyingRef = useRef(false);
    const applyCaseRef = useRef(applyCase);
    applyCaseRef.current = applyCase;

    const closePreview = () => {
        requestRef.current += 1;
        isApplyingRef.current = false;
        setSession(null);
        setAnalysis(null);
        setIsAnalyzing(false);
        setIsApplying(false);
    };

    const buildQueuedSession = (fromSession) => {
        const [next, ...rest] = fromSession?.queue || [];

        if (!next) {
            return null;
        }

        return {
            messageId: next.messageId,
            intent: next.intent || fromSession.intent,
            existingCases: next.existingCases || [],
            message: next.message || null,
            queue: rest,
            batchIndex: (fromSession.batchIndex || 0) + 1,
            batchTotal: fromSession.batchTotal,
        };
    };

    const applyFromSession = async (nextSession, nextAnalysis) => {
        if (isApplyingRef.current || !applyCaseRef.current) {
            return false;
        }

        isApplyingRef.current = true;
        setIsApplying(true);

        try {
            const created = await applyCaseRef.current({
                messageId: nextSession.messageId,
                intent: nextSession.intent,
                existingCases: nextSession.existingCases,
                message: nextSession.message,
                analysis: nextAnalysis,
            });

            return Boolean(created);
        } finally {
            isApplyingRef.current = false;
            setIsApplying(false);
        }
    };

    const runSession = async (nextSession) => {
        const requestId = requestRef.current + 1;
        requestRef.current = requestId;
        setSession(null);
        setAnalysis(null);
        setIsAnalyzing(true);

        try {
            const payload = await service.analyzeMessage(nextSession.messageId, {
                intent: nextSession.intent,
            });

            if (requestRef.current !== requestId) {
                return;
            }

            const normalized = normalizeMailboxAnalysis(payload, nextSession.intent);

            if (!normalized) {
                throw new Error('Invalid analysis');
            }

            if (isMailboxAnalysisNotRelevant(normalized)) {
                setSession(nextSession);
                setAnalysis(normalized);
                return;
            }

            const created = await applyFromSession(nextSession, normalized);

            if (requestRef.current !== requestId) {
                return;
            }

            if (created) {
                const queued = buildQueuedSession(nextSession);

                if (queued) {
                    await runSession(queued);
                    return;
                }
            }

            closePreview();
        } catch (analyzeError) {
            if (requestRef.current !== requestId) {
                return;
            }

            if (process.env.NODE_ENV === 'development') {
                console.error(analyzeError);
            }

            toast.error(mapMailboxAnalyzeError(analyzeError, t));
            closePreview();
        } finally {
            if (requestRef.current === requestId) {
                setIsAnalyzing(false);
            }
        }
    };

    const startPreview = ({
        messageId,
        intent,
        existingCases = [],
        message = null,
        queue = [],
        batchIndex = 0,
        batchTotal,
    }) => {
        if (!messageId || !intent) {
            return;
        }

        if (findMailboxLinkedCase(existingCases, intent)) {
            return;
        }

        if (mailboxMessageHasLinkedCase(message, existingCases)) {
            toast.error(t('A case has already been created from this email.'));
            return;
        }

        runSession({
            messageId,
            intent,
            existingCases,
            message,
            queue,
            batchIndex,
            batchTotal: batchTotal || 1 + (queue?.length || 0),
        });
    };

    const startPreviewForItems = (items, intent) => {
        const itemsWithId = (items || []).filter((item) => item?.messageId);
        const targets = itemsWithId.filter(({ existingCases = [], message } = {}) => (
            !mailboxMessageHasLinkedCase(message, existingCases)
        ));

        if (!itemsWithId.length) {
            toast.info(t('Select at least one message.'));
            return;
        }

        if (!targets.length) {
            toast.info(t('Selected messages already have a linked case.'));
            return;
        }

        const [first, ...rest] = targets;

        startPreview({
            messageId: first.messageId,
            intent,
            existingCases: first.existingCases || [],
            message: first.message || null,
            queue: rest.map((item) => ({
                messageId: item.messageId,
                existingCases: item.existingCases || [],
                message: item.message || null,
                intent,
            })),
        });
    };

    const handleCancel = () => {
        closePreview();
    };

    const handleConfirm = async () => {
        if (!session || isApplyingRef.current || isAnalyzing || !analysis) {
            return;
        }

        const currentSession = session;
        const created = await applyFromSession(currentSession, analysis);

        if (!created) {
            return;
        }

        const queued = buildQueuedSession(currentSession);

        if (queued) {
            await runSession(queued);
            return;
        }

        closePreview();
    };

    return {
        intents: resolvedIntents,
        session,
        analysis,
        isOpen: Boolean(session) && isMailboxAnalysisNotRelevant(analysis),
        isAnalyzing,
        isApplying,
        isBusy: Boolean(session) || isAnalyzing || isApplying,
        startPreview,
        startPreviewForItems,
        handleCancel,
        handleConfirm,
    };
};

export default useMailboxIntentPreview;
